import {
  isAttentionStatus,
  isTimesheetEditable,
  type Timesheet,
} from '@clockwise/clients/timesheets';
import { AlertCircle, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ApprovalResolutionNote, attentionBannerClass } from './approval-resolution-note';
import { approvalResolution, approvalResolutionText } from './approval-resolution-text';
import { ApprovalStatusBadge } from './approval-status-badge';
import { BillableBadge } from './timesheet-billable';
import { type CellEditorValue, TimesheetCellEditor } from './timesheet-cell-editor';
import { formatHours, longDayName, shortDate } from './timesheet-week';

/**
 * The single glanceable marker for ONE entry, shared by the week-grid cell
 * face and the popover so the two can't drift (review finding: the precedence
 * lived in two places). Precedence: attention (rejected/failed) then
 * missing-description. Billable is intentionally NOT surfaced — a cell can't
 * legibly carry it, and a wrong glanceable signal is worse than none. Returns
 * `null` when there's nothing to flag.
 */
export function EntryStatusDot({ entry, className }: { entry: Timesheet; className?: string }) {
  if (isAttentionStatus(entry.status)) {
    return (
      <ApprovalStatusBadge
        status={entry.status}
        reason={approvalResolutionText(entry.resolutionReason)}
        size="dot"
        className={className}
      />
    );
  }
  if (Number(entry.hours ?? 0) > 0 && !entry.description?.trim()) {
    return (
      <span
        aria-hidden
        title="Missing description"
        className={cn('inline-block size-1.5 shrink-0 rounded-full bg-red-500', className)}
      />
    );
  }
  return null;
}

interface Props {
  date: string;
  /** The single entry this cell holds, or `null` for an empty cell (which
   *  opens straight into the add form). A cell never holds more than one
   *  entry — extra entries for the same (customer, product, date) live on
   *  their own duplicate rows. */
  entry: Timesheet | null;
  /** Internal customers are never billable — forces the editor's toggle off. */
  customerIsInternal?: boolean;
  /** Default billable for a NEW entry (sticky from the day's other same-pair
   *  entries). Ignored when editing an existing entry. */
  defaultBillable?: boolean;
  /** Pre-fills the description on a NEW entry (from the row's product). */
  productDescription?: string | null;
  /** Submit-time field error for this entry (missing hours OR description) —
   *  shown inline in the editor so opening a flagged cell points at the fix. */
  submitError?: string;
  /** Sum of the day's OTHER entries (excluding this cell's), for the inline
   *  24h/day cap. */
  otherHoursOnDay?: number;
  saving?: boolean;
  /** Read-only mode for a period-locked cell: the entry renders read-only (so
   *  the rejection reason etc. stay inspectable) and there's no editor. The
   *  grid still lets the user OPEN a locked cell — they just can't edit. */
  readOnly?: boolean;
  /** Create a new entry in this cell. */
  onCreate: (value: CellEditorValue) => void;
  /** Patch the entry with `id`. */
  onPatch: (id: string, value: CellEditorValue) => void;
  /** Hard-delete the entry with `id` (draft / rejected / failed only). */
  onDelete: (id: string) => void;
  onClose: () => void;
  /** Reports whether the editor holds unsaved edits (forwarded from
   *  {@link TimesheetCellEditor}); only fires in editable mode. */
  onDirtyChange?: (dirty: boolean) => void;
}

/**
 * Popover body for a single week-grid day cell.
 *
 * A cell holds at most one entry, so this is a thin wrapper over
 * {@link TimesheetCellEditor}: an empty cell opens the add form; an editable
 * entry (draft / rejected / failed) opens the editor seeded from it; a locked
 * entry (submitted / approved) or a period-locked cell renders read-only via
 * {@link LockedEntryView} so the rejection reason / details stay inspectable.
 * To log a second block of work on the same (customer, product, date) the user
 * adds another row — the cell itself never multiplexes entries.
 */
export function CellEntryEditor({
  date,
  entry,
  customerIsInternal,
  defaultBillable,
  productDescription,
  submitError,
  otherHoursOnDay,
  saving,
  readOnly,
  onCreate,
  onPatch,
  onDelete,
  onClose,
  onDirtyChange,
}: Props) {
  // Editable unless period-locked or the entry is submitted/approved.
  const editable = !readOnly && (entry === null || isTimesheetEditable(entry.status));

  if (editable) {
    return (
      <TimesheetCellEditor
        onDirtyChange={onDirtyChange}
        date={date}
        entry={entry}
        defaultBillable={entry?.billable ?? defaultBillable ?? false}
        customerIsInternal={customerIsInternal}
        defaultDescription={productDescription}
        submitError={submitError}
        otherHoursOnDay={otherHoursOnDay}
        saving={saving}
        onSave={(value) => {
          if (entry) {
            onPatch(entry.id, value);
            return;
          }
          // A truly blank "Add" (no hours AND no description) is a no-op — we
          // don't persist a phantom empty entry. But a description with no
          // hours yet IS worth saving: it keeps the note, renders the cell as
          // "0h" so the user can find it and fill the hours in later, and the
          // entry is flagged "missing hours" at submit until they do.
          if (value.hours == null && !value.description?.trim()) {
            onClose();
            return;
          }
          onCreate(value);
        }}
        onClear={() => {
          if (entry) onDelete(entry.id);
        }}
        onClose={onClose}
      />
    );
  }

  // Read-only: a period-locked cell, or a submitted/approved entry.
  return entry ? <LockedEntryView date={date} entry={entry} periodLocked={readOnly} /> : null;
}

/** Read-only view for a single entry — used for submitted/approved entries and
 *  for a period-locked cell (`periodLocked`). Surfaces the status word + the
 *  rejection/failure reason so a period-locked rejected entry's reason stays
 *  readable. */
function LockedEntryView({
  date,
  entry,
  periodLocked,
}: {
  date: string;
  entry: Timesheet;
  periodLocked?: boolean;
}) {
  // Only the period-lock case carries an explanatory note. Submitted/approved
  // entries surface their status via the pill + the Recall action, so no
  // extra "— locked" line is shown.
  const note = periodLocked ? 'This period is locked and can no longer be edited.' : null;
  // Classified from the entry's own status, which is why this view needs no
  // rejection-details fetch: `rejected` is reachable only from a decision a
  // person made, `failed` only from the approval system giving up. See
  // `approvalResolution`.
  const resolution = approvalResolution(entry.resolutionReason, entry.status);
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          {longDayName(date).slice(0, 3)}, {shortDate(date)}
        </div>
        <span
          className={cn(
            'inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide',
            // Mirror the week grid's lock colours: period locked → red,
            // approved → green, submitted / other → neutral grey.
            periodLocked
              ? 'text-red-600 dark:text-red-500'
              : entry.status === 'approved'
                ? 'text-green-600 dark:text-green-500'
                : 'text-muted-foreground',
          )}
        >
          <Lock className="size-3" /> {periodLocked ? 'locked' : entry.status}
        </span>
      </div>
      <div className="rounded-md border bg-muted/40 px-2.5 py-2 text-sm">
        <div className="flex items-center justify-between tabular-nums">
          <span className="font-medium">
            {entry.hours != null ? formatHours(Number(entry.hours)) : '—'}
          </span>
          <BillableBadge billable={entry.billable} />
        </div>
        {entry.description?.trim() ? (
          <p className="mt-1 leading-snug text-muted-foreground">{entry.description}</p>
        ) : null}
      </div>
      {/* Rejection / failure reason — set only on rejected/failed entries.
          The whole point of opening a locked cell read-only is to read this. */}
      {resolution ? (
        <div
          className={cn(
            'rounded-md border px-2.5 py-1.5 text-[11px]',
            attentionBannerClass(entry.status),
          )}
        >
          <ApprovalResolutionNote resolution={resolution} />
        </div>
      ) : null}
      {note ? (
        <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <AlertCircle className="size-3.5 shrink-0" />
          {note}
        </p>
      ) : null}
    </div>
  );
}
